/**
 * 🛰️ IsochroneCabinet v1.2.0 (Sovereign Share & QR)
 * 職責：精確分時多邊形渲染、線路交集、以及「主權分享」機制 (短網址 + QR Code)
 */

import { SovereignCabinet } from '../../core/cabinet-base.js';
import { SovereignEvents } from '../../core/cabinet-registry.js';
import { DEFAULT_QR_LOGO_MODE, ensureQrLibrary, renderBrandedQr } from '../../shared/brand-qr.js';
import { autocompleteSuggestionView } from '../../shared/autocomplete-suggestion-label.js';
import {
    SCREENSHOT_FORMATS,
    SCREENSHOT_PRINT_SCALE,
    SCREENSHOT_LAYOUT_DPI,
    BUS_ROUTE_LIST_LAYOUT,
    clampBusRouteListHeightPx,
    allocatePopTableColPercents,
    formatPixelSize,
    mmToPx,
    ptToPx,
    computeIsoDynamicViewport,
    boundsFromGeoFeatures,
    captureMapContainer,
    downloadCanvasPng,
    waitForMapTiles,
    waitForPaintFrames,
    injectDistrictBoundariesToGhost,
    LIGHT_TRANSPORT_TILE_URL,
    LIGHT_CARTO_TILE_URL,
    POP_TABLE_HEADER_STYLE,
    POP_TABLE_FONT_STACK,
    ensureScreenshotFontsLoaded
} from '../../utils/sovereign-map-capture.js';
import {
    ISOCHRONE_BUS_RING_SECONDS,
    harvestBusStopsByIsochroneRings,
    stopLonLat,
    pillTextColor,
    filterRouteFeatureToIsochroneParts
} from '../../utils/isochrone-bus-stop-harvest.js';
import { busRouteDetailsMatch, busRouteFetchIds } from '../../utils/bus-route-identity.js';

function leaflet() {
    return typeof window !== 'undefined' ? window.L : null;
}

class IsochroneCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.loadAssets('/js/cabinets/isochrone');
        
        // 秒／公尺 → 色（時間：步行 2–12 分、單車／開車 10–50 分；距離：400–2000m）
        this.STYLE_CONFIG = {
            120: { color: '#d73027', dash: null },
            240: { color: '#fc8d59', dash: '10 6' },
            360: { color: '#fee08b', dash: '2 6' },
            480: { color: '#91bfdb', dash: '14 6 2 6' },
            600: { color: '#4575b4', dash: '18 6' },
            720: { color: '#7b3294', dash: '10 6' },
            1200: { color: '#fc8d59', dash: '10 6' },
            1800: { color: '#fee08b', dash: '2 6' },
            2400: { color: '#91bfdb', dash: '14 6 2 6' },
            3000: { color: '#7b3294', dash: '10 6' },
            400: { color: '#d73027', dash: null },
            800: { color: '#fc8d59', dash: '10 6' },
            1600: { color: '#4575b4', dash: '18 6' },
            2000: { color: '#7b3294', dash: '10 6' }
        };

        this.TRAVEL_MODES = {
            'foot-walking': {
                label: '步行',
                unit: 'time',
                ranges: [120, 240, 360, 480, 600, 720],
                defaultMax: 720
            },
            'cycling-regular': {
                label: '單車',
                unit: 'time',
                ranges: [600, 1200, 1800, 2400, 3000],
                defaultMax: 3000
            },
            'driving-car': {
                label: '開車',
                unit: 'time',
                ranges: [600, 1200, 1800, 2400, 3000],
                defaultMax: 3000
            },
            'foot-distance-2km': {
                label: '兩公里步行',
                unit: 'distance',
                ranges: [400, 800, 1200, 1600, 2000],
                defaultMax: 2000
            }
        };

        this.isoLayerGroup = null;
        this.routeLayerGroup = null;
        this.routeLayersByKey = new Map();
        this.busOverlayOn = false;
        this._busOverlayToken = 0;
        this.currentData = null;
        this.currentRelPath = null;
        /** @type {Record<string, string>} modeId → 可分享相對路徑 */
        this.modeRelPaths = {};
        /** @type {Record<string, string>} modeId → 已建立的分享 hash */
        this.shareHashByMode = {};
        this.shareHash = null;
        this.omniResults = null;
        this.activeTravelMode = 'foot-walking';
    }

    /** 合併後端 savedPaths；並把 currentRelPath 對齊目前模式 */
    applyModeSharePaths(paths = {}, { preferMode = null } = {}) {
        const next = { ...(this.modeRelPaths || {}) };
        for (const [modeId, rel] of Object.entries(paths || {})) {
            if (rel && this.TRAVEL_MODES[modeId]) next[modeId] = String(rel);
        }
        this.modeRelPaths = next;
        const mode = preferMode || this.activeTravelMode;
        this.currentRelPath = this.modeRelPaths[mode] || null;
        this.shareHash = this.shareHashByMode?.[mode] || null;
    }

    /** 從 geojson metadata／圈值推斷旅行模式（開啟分享連結用） */
    detectTravelModeFromGeo(geo) {
        const sov = geo?.metadata?.sovereign || {};
        if (sov.modeId && this.TRAVEL_MODES[sov.modeId]) return sov.modeId;
        const nameMap = {
            步行: 'foot-walking',
            自行車: 'cycling-regular',
            單車: 'cycling-regular',
            開車: 'driving-car',
            兩公里步行: 'foot-distance-2km'
        };
        if (sov.mode && nameMap[sov.mode]) return nameMap[sov.mode];
        if (sov.range_type === 'distance' || Number(sov.ringsM?.[0]) === 400) {
            return 'foot-distance-2km';
        }
        const values = (geo?.features || [])
            .map((f) => Number(f.properties?.value))
            .filter((n) => Number.isFinite(n));
        if (values.some((v) => v > 0 && v <= 720 && v % 60 === 0 && v < 600)) {
            return 'foot-walking';
        }
        if (values.includes(120) || values.includes(240) || values.includes(360)) {
            return 'foot-walking';
        }
        return this.activeTravelMode || 'foot-walking';
    }

    onMount() {
        this.initControls();
        this.initSearch();
        this.loadQrLibrary();

        // [v17.5.2] 響應截圖模式切換 (模式 A 需無填充)
        this.bus.on(SovereignEvents.SCREENSHOT_MODE_CHANGE, (data) => {
            if (!this.isoLayerGroup) return;
            const targetOpacity = (data.fillOpacity === 'restore') ? null : data.fillOpacity;
            
            this.isoLayerGroup.eachLayer(l => {
                const finalOp = (targetOpacity === null) ? (l.options._originalOpacity || 0.2) : targetOpacity;
                l.eachLayer(sub => sub.setStyle({ fillOpacity: finalOp }));
            });
        });
    }

    onUnmount() {
        this.clearAll();
    }

    onMapReady(map) {
        this.map = map;
        this.createLegendBox();
        this.openSharedHash();
    }

    afterAssetsMounted() {
        this.openSharedHash();
    }

    /**
     * /share?h= 會轉到 /?h=。在完整地圖上載入該圈，櫃與其他功能照常可用。
     */
    async openSharedHash() {
        if (this._shareOpened || !this.map || !this.shadowRoot?.getElementById('status-text')) return;
        const hash = new URLSearchParams(window.location.search).get('h') || '';
        if (!/^[a-f0-9]{16}$/.test(hash)) return;
        this._shareOpened = true;
        document.getElementById('socket-isochrone-container')?.classList.remove('collapsed');
        this.setStatus('📂 正在開啟分享的分時圈…');
        try {
            const geo = await this.fetchIsoJson(`/api/isochrones/share/${hash}`);
            const modeId = this.detectTravelModeFromGeo(geo);
            this.activeTravelMode = modeId;
            this.omniResults = { [modeId]: geo };
            this.currentData = geo;
            this.shareHash = hash;
            this.shareHashByMode = { [modeId]: hash };
            this.modeRelPaths = {};
            this.currentRelPath = null;
            const label = geo?.metadata?.sovereign?.label
                || geo?.metadata?.sovereign?.address
                || '';
            const input = this.shadowRoot.getElementById('isochroneSearch');
            if (input && label) input.value = label;
            this.fillTimeRangeOptions(modeId);
            this.syncTravelModeButtons();
            const maxSec = parseInt(
                this.shadowRoot.getElementById('timeRangeScale')?.value
                || String(this.TRAVEL_MODES[modeId]?.defaultMax || 720),
                10
            );
            this.renderIsochroneLayers(geo, maxSec);
            const modeLabel = this.TRAVEL_MODES[modeId]?.label || modeId;
            this.setStatus(
                label
                    ? `✅ 已開啟分享（${modeLabel}）：${label}`
                    : `✅ 已開啟分享的分時圈（${modeLabel}）`
            );
        } catch (err) {
            this._shareOpened = false;
            this.setStatus(`❌ 分享連結無法開啟: ${err.message}`);
        }
    }

    /**
     * 動態加載本地 QR Code 函式庫 (Sovereign Local-First)
     */
    async loadQrLibrary() {
        return ensureQrLibrary();
    }

    initControls() {
        const shadow = this.shadowRoot;
        const select = shadow.getElementById('timeRangeScale');

        this.fillTimeRangeOptions(this.activeTravelMode);

        shadow.querySelectorAll('[data-travel-mode]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-travel-mode');
                this.setTravelMode(mode);
            });
        });

        shadow.getElementById('btnDrawCentro').addEventListener('click', () => {
            this.drawFromMapCenterOrAddress().catch((err) => {
                this.setStatus(`❌ 繪製失敗: ${err.message || err}`);
            });
        });

        shadow.getElementById('btnClear').addEventListener('click', () => {
            this.clearAll();
            this.setStatus('已重設圖層');
        });

        shadow.getElementById('btnToggleFill').addEventListener('click', () => {
            this.toggleFill();
        });

        shadow.getElementById('btnExport').addEventListener('click', () => {
            this.toggleBusOverlay().catch((err) => {
                this.setStatus(`❌ 公車路線失敗: ${err.message || err}`);
            });
        });

        shadow.getElementById('btnShare').addEventListener('click', () => {
            this.shareIsochrone();
        });

        shadow.getElementById('btnCloseShare').addEventListener('click', () => {
            shadow.getElementById('shareOverlay').style.display = 'none';
        });

        shadow.getElementById('btnCopyUrl').addEventListener('click', async () => {
            const url = shadow.getElementById('shareUrl').value;
            try {
                await navigator.clipboard.writeText(url);
                this.setStatus('✅ 連結已複製');
            } catch {
                this.setStatus('❌ 複製失敗');
            }
        });

        shadow.getElementById('btnDownloadQr')?.addEventListener('click', () => {
            const canvas = shadow.getElementById('qrContainer')?.querySelector('canvas');
            if (!canvas) {
                this.setStatus('❌ 沒有 QR 圖');
                return;
            }
            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = '分時圈分享.png';
            a.click();
        });

        select.addEventListener('change', () => {
            if (this.currentData) {
                this.renderIsochroneLayers(this.currentData, parseInt(select.value, 10));
            }
        });
    }

    fillTimeRangeOptions(modeId) {
        const select = this.shadowRoot?.getElementById('timeRangeScale');
        const meta = this.TRAVEL_MODES[modeId] || this.TRAVEL_MODES['foot-walking'];
        if (!select) return;
        const prev = parseInt(select.value, 10);
        const isDist = meta.unit === 'distance';
        select.innerHTML = meta.ranges
            .map((v) => {
                const selected = v === (meta.ranges.includes(prev) ? prev : meta.defaultMax)
                    ? ' selected'
                    : '';
                const label = isDist ? `${v} 公尺` : `${v / 60} 分鐘`;
                return `<option value="${v}"${selected}>${label}</option>`;
            })
            .join('');
    }

    isDistanceMode(modeId = this.activeTravelMode) {
        return this.TRAVEL_MODES[modeId]?.unit === 'distance';
    }

    formatRangeLabel(value, modeId = this.activeTravelMode) {
        const n = Number(value);
        if (!Number.isFinite(n)) return '';
        return this.isDistanceMode(modeId) ? `${n}m` : `${n / 60} 分`;
    }

    setTravelMode(modeId) {
        if (!this.TRAVEL_MODES[modeId]) return;
        this.activeTravelMode = modeId;
        this.shadowRoot?.querySelectorAll('[data-travel-mode]').forEach((btn) => {
            btn.classList.toggle('is-active', btn.getAttribute('data-travel-mode') === modeId);
        });
        this.fillTimeRangeOptions(modeId);
        this.currentRelPath = this.modeRelPaths?.[modeId] || null;
        this.shareHash = this.shareHashByMode?.[modeId] || null;
        const geo = this.omniResults?.[modeId]
            || (modeId === 'foot-walking' && !this.isDistanceMode(modeId) ? this.currentData : null);
        if (!geo) {
            this.setStatus(`⚠️ 尚無「${this.TRAVEL_MODES[modeId].label}」圈（無圈灰框；請先繪製）`);
            return;
        }
        this.currentData = geo;
        const maxVal = parseInt(this.shadowRoot.getElementById('timeRangeScale')?.value
            || String(this.TRAVEL_MODES[modeId].defaultMax), 10);
        this.renderIsochroneLayers(geo, maxVal);
        const shareHint = this.currentRelPath || this.shareHash ? '' : ' · 此模式尚無可分享檔';
        this.setStatus(
            `✅ 顯示${this.TRAVEL_MODES[modeId].label}（≤${this.formatRangeLabel(maxVal, modeId)}）${shareHint}`
        );
    }

    syncTravelModeButtons() {
        this.shadowRoot?.querySelectorAll('[data-travel-mode]').forEach((btn) => {
            const id = btn.getAttribute('data-travel-mode');
            btn.disabled = !this.omniResults?.[id];
            btn.classList.toggle('is-active', id === this.activeTravelMode);
        });
    }

    async initSearch() {
        const input = this.shadowRoot.getElementById('isochroneSearch');
        const suggestionBox = this.shadowRoot.getElementById('searchSuggestions');
        if (!input || !suggestionBox) return;

        let debounceTimer = null;
        input.addEventListener('input', async (e) => {
            const val = e.target.value.trim();
            if (val.length < 2) {
                suggestionBox.innerHTML = '';
                return;
            }

            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                try {
                    this.setStatus(`🔍 搜尋「${val}」…`);
                    const res = await fetch(`/api/autocomplete?q=${encodeURIComponent(val)}&limit=12`, {
                        credentials: 'include'
                    });
                    if (res.status === 401) {
                        suggestionBox.innerHTML = '';
                        this.setStatus('❌ 請先登入後再搜尋社區／建案');
                        return;
                    }
                    if (!res.ok) {
                        suggestionBox.innerHTML = '';
                        this.setStatus(`❌ 搜尋失敗 HTTP ${res.status}`);
                        return;
                    }
                    const data = await res.json();

                    suggestionBox.innerHTML = '';
                    const list = Array.isArray(data) ? data : [];
                    if (!list.length) {
                        const hint = document.createElement('div');
                        hint.className = 'suggestion-item';
                        const meta = document.createElement('div');
                        meta.className = 'suggestion-meta';
                        meta.textContent = '找不到社區，也沒有曾調閱的門牌';
                        hint.appendChild(meta);
                        suggestionBox.appendChild(hint);
                        this.setStatus(`⚠️ 「${val}」無結果`);
                        return;
                    }
                    list.forEach((item) => {
                        const div = document.createElement('div');
                        div.className = 'suggestion-item';
                        const view = autocompleteSuggestionView(item);
                        const label = view.label;
                        const main = document.createElement('div');
                        main.className = 'suggestion-main';
                        main.textContent = view.main;
                        const meta = document.createElement('div');
                        meta.className = 'suggestion-meta';
                        meta.textContent = view.meta;
                        div.appendChild(main);
                        div.appendChild(meta);
                        div.onclick = () => {
                            input.value = label;
                            suggestionBox.innerHTML = '';
                            this.handleAddressSelection({
                                ...item,
                                display: label,
                                lat: item.lat,
                                lng: item.lng != null ? item.lng : item.lon
                            });
                        };
                        suggestionBox.appendChild(div);
                    });
                    const doors = list.filter((item) => item.kind === 'doorplate').length;
                    this.setStatus(doors
                        ? `🔍 社區 ${list.length - doors} · 曾調閱 ${doors}`
                        : `🔍 找到 ${list.length} 筆，請點選`);
                } catch (err) {
                    console.warn('[SovereignSearch] Autocomplete 失敗:', err.message);
                    suggestionBox.innerHTML = '';
                    this.setStatus(`❌ 搜尋異常: ${err.message}`);
                }
            }, 300);
        });
    }

    /**
     * 處理地址選中後的連動 (跳轉 + 學區 + 專家卡)
     */
    async handleAddressSelection(item) {
        this.setStatus(`🎯 正在定位: ${item.display}`);

        if (item.lat && item.lng) {
            window.appState = window.appState || {};
            window.appState.lastSelectedTarget = item.display || item.address || item.name || '';
            window.appState.lastSelectedHAid = item.h_aid_11 || item.hAid || null;
            if (item.city) window.appState.currentCity = item.city;
            if (item.district) window.appState.currentDistrict = item.district;
            if (item.h_community_id) {
                window.appState.lastCommunityId = item.h_community_id;
                window.appState.lastCommunityName = item.display || item.name || null;
            } else {
                window.appState.lastCommunityName = null;
            }
            this.map.flyTo([item.lat, item.lng], 16);
        if (item.relPath && (item.kind === 'doorplate' || item.previouslyFetched)) {
            if (item.kind === 'doorplate') {
                window.appState.lastCommunityId = null;
                window.appState.lastCommunityName = null;
            }
            try {
                // 重開曾調閱門牌須重建整組模式；禁止沿用上一個定點的 omniResults。
                this.omniResults = null;
                this.activeTravelMode = 'foot-walking';
                this.syncTravelModeButtons();
                const distancePayload = {
                    lat: item.lat,
                    lng: item.lng,
                    address: item.address || item.display,
                    city: item.city || '',
                    district: item.district || '',
                    h_aid_11: item.h_aid_11 || null,
                    targetName: item.display
                };
                const [timeSettled, distSettled] = await Promise.allSettled([
                    this.fetchIsoJson(`/api/isochrones/recall-omni?path=${encodeURIComponent(item.relPath)}`),
                    this.fetchIsoJson('/api/isochrones/ensure-walk-distance', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(distancePayload)
                    })
                ]);
                if (timeSettled.status !== 'fulfilled') throw timeSettled.reason;
                const recalled = timeSettled.value;
                this.omniResults = { ...(recalled.results || {}) };
                this.shareHashByMode = {};
                this.shareHash = null;
                const pathBag = { ...(recalled.savedPaths || {}) };
                if (item.relPath) pathBag['foot-walking'] = item.relPath;
                if (distSettled.status === 'fulfilled' && distSettled.value?.geojson) {
                    this.omniResults['foot-distance-2km'] = distSettled.value.geojson;
                    if (distSettled.value.h_aid_11) {
                        Object.assign(window.appState, {
                            lastSelectedHAid: distSettled.value.h_aid_11
                        });
                    }
                    if (distSettled.value.savedPath) {
                        pathBag['foot-distance-2km'] = distSettled.value.savedPath;
                    } else if (distSettled.value.savedPaths?.['foot-distance-2km']) {
                        pathBag['foot-distance-2km'] = distSettled.value.savedPaths['foot-distance-2km'];
                    }
                }
                this.applyModeSharePaths(pathBag, { preferMode: 'foot-walking' });
                const geo = this.omniResults['foot-walking'];
                if (!geo) throw new Error('舊檔缺少步行圈');
                this.currentData = geo;
                this.fillTimeRangeOptions('foot-walking');
                const maxSec = parseInt(
                    this.shadowRoot.getElementById('timeRangeScale')?.value || '720',
                    10
                );
                this.renderIsochroneLayers(geo, maxSec);
                this.syncTravelModeButtons();
                const modes = Object.keys(this.omniResults).length;
                const distNote = this.omniResults['foot-distance-2km']
                    ? ' · 兩公里已載入'
                    : ' · 兩公里無檔';
                this.setStatus(`✅ 已載入曾調閱：${item.display} · ${modes} 模式${distNote}`);
                return;
            } catch (err) {
                this.setStatus(`⚠️ 讀不到舊檔，改向 ORS 重算：${err.message}`);
            }
        }

        const minutes = parseInt(this.shadowRoot.getElementById('timeRangeScale')?.value || '720', 10) / 60;
            this.requestIsochrone(item.lat, item.lng);
        } else {
            this.setStatus('❌ 此筆無座標，無法繪製分時圈');
            return;
        }

        try {
            const expertRes = await fetch(`/api/expert-referral?address=${encodeURIComponent(item.display || '')}`, {
                credentials: 'include'
            });
            if (expertRes.ok) {
                const expert = await expertRes.json();
                if (expert) this.showExpertCard(expert);
            }
        } catch {
            /* 專家卡非關鍵路徑 */
        }
    }

    showExpertCard(expert) {
        const shadow = this.shadowRoot;
        let card = shadow.getElementById('expertCard');
        if (!card) {
            card = document.createElement('div');
            card.id = 'expertCard';
            card.className = 'expert-recommendation-card';
            shadow.appendChild(card);
        }

        card.innerHTML = `
            <div class="expert-header">⚠️ 注意：本校為滿額學區</div>
            <div class="expert-body">
                <p>${expert.slogan}</p>
                <div class="expert-profile">
                    <div class="expert-name">${expert.name}</div>
                    <div class="expert-company">${expert.company}</div>
                    <div class="expert-phone">📞 ${expert.phone}</div>
                </div>
                <a href="${expert.lineLink}" target="_blank" class="btn-line-add">🟢 加 LINE 諮詢 (ID: ${expert.lineId})</a>
            </div>
        `;
        card.style.display = 'block';
    }

    /**
     * @param {string} text
     * @param {{ tone?: 'default'|'warn'|'emergency' }} [opts]
     */
    setStatus(text, opts = {}) {
        const el = this.shadowRoot.getElementById('status-text');
        if (!el) return;
        el.innerText = text;
        el.classList.remove('is-warn', 'is-emergency');
        if (opts.tone === 'warn') el.classList.add('is-warn');
        if (opts.tone === 'emergency') el.classList.add('is-emergency');
    }

    clearEmergencyWaitTimer() {
        if (this._orsEmergencyWaitTimer) {
            clearTimeout(this._orsEmergencyWaitTimer);
            this._orsEmergencyWaitTimer = null;
        }
    }

    /** 長時間等待：黃字提醒；確認走應急後才改紅字（見 requestIsochrone） */
    armEmergencyWaitHint(modeLabels = ['步行', '單車', '開車', '兩公里步行']) {
        this.clearEmergencyWaitTimer();
        const list = modeLabels.filter(Boolean).join('／');
        this._orsEmergencyWaitTimer = setTimeout(() => {
            this.setStatus(
                `⏳ ${list} 仍在計算；若主站失效並改打應急 ORS，冷啟可能需數分鐘…`,
                { tone: 'warn' }
            );
        }, 12000);
    }

    formatBackupModeLabels(modeIds = []) {
        return modeIds
            .map((id) => this.TRAVEL_MODES[id]?.label || id)
            .filter(Boolean);
    }

    async loadStoredIsochrone(item) {
        this.setStatus(`📂 正在載入 ${item.filename}...`);
        try {
            const geojson = await this.fetchIsoJson(`/api/isochrones/isochrone?path=${encodeURIComponent(item.relPath)}`);
            const modeId = this.detectTravelModeFromGeo(geojson);
            this.activeTravelMode = modeId;
            this.omniResults = { ...(this.omniResults || {}), [modeId]: geojson };
            this.currentData = geojson;
            this.applyModeSharePaths({ [modeId]: item.relPath }, { preferMode: modeId });
            this.fillTimeRangeOptions(modeId);
            this.syncTravelModeButtons();
            const select = this.shadowRoot.getElementById('timeRangeScale');
            this.renderIsochroneLayers(geojson, parseInt(select.value, 10));
            this.setStatus(`✅ 已載入資產: ${item.filename}`);
        } catch (err) {
            this.setStatus(`❌ 載入失敗: ${err.message}`);
        }
    }

    /**
     * [Automation API] 外部調用接口
     */
    triggerAnalysis(options = {}) {
        const { lat, lng } = options;
        const targetLat = lat || (this.map ? this.map.getCenter().lat : 25.026);
        const targetLng = lng || (this.map ? this.map.getCenter().lng : 121.465);
        return this.requestIsochrone(targetLat, targetLng);
    }

    async fetchIsoJson(url, options = {}) {
        const headers = { ...(options.headers || {}) };
        // JWT 僅 httpOnly Cookie（credentials: include）；不再讀 localStorage Bearer（S-07）
        const response = await fetch(url, {
            credentials: 'include',
            ...options,
            headers
        });
        let result = {};
        try {
            result = await response.json();
        } catch {
            result = {};
        }
        if (!response.ok) {
            const err = new Error(result.error || result.message || `HTTP ${response.status}`);
            if (result.diagId) err.diagId = result.diagId;
            if (result.code) err.code = result.code;
            throw err;
        }
        return result;
    }

    /**
     * 「以地圖中心繪製」：若搜尋框有門牌且非已選社區，先 geocode 飛到門牌再產圈
     */
    async drawFromMapCenterOrAddress() {
        if (!this.map) {
            this.setStatus('❌ 地圖尚未就緒');
            return;
        }
        const typed = String(this.shadowRoot.getElementById('isochroneSearch')?.value || '').trim();
        const selected = String(window.appState?.lastSelectedTarget || '').trim();
        const communityId = window.appState?.lastCommunityId || null;
        const communitySelected = !!(
            communityId
            && selected
            && typed === selected
        );
        // 里／村／新村為一級路名時無「路街巷弄」（DOORPLATE_VILLAGE_AS_ROAD_SPEC）
        const doorplateTyped = (
            /[路街道巷弄]/.test(typed)
            || /[村里]/.test(typed)
            || /新村/.test(typed)
        ) && /[號\d]/.test(typed);

        if (typed.length >= 3 && doorplateTyped && !communitySelected) {
            this.setStatus(`🔍 定位門牌「${typed}」…`);
            const hit = await this.geocodeDoorplate(typed);
            if (!(hit?.lat != null && hit?.lng != null)) {
                this.setStatus(`❌ 無法定位門牌「${typed}」，未繪製（不會改用地圖中心）`);
                return;
            }
            window.appState = window.appState || {};
            window.appState.lastSelectedTarget = hit.name || typed;
            window.appState.lastCommunityId = null;
            window.appState.lastSelectedHAid = hit.h_aid_11 || null;
            if (hit.city) window.appState.currentCity = hit.city;
            if (hit.district) window.appState.currentDistrict = hit.district;
            this.map.setView([hit.lat, hit.lng], Math.max(this.map.getZoom() || 16, 16), { animate: false });
            this.setStatus(`🎯 已將門牌置中，正在繪製分時圈…`);
            await this.requestIsochrone(hit.lat, hit.lng);
            return;
        }

        const center = this.map.getCenter();
        await this.requestIsochrone(center.lat, center.lng);
    }

    /** 門牌 → 座標：TGOS 優先，Nominatim 備援 */
    async geocodeDoorplate(q) {
        const query = String(q || '').trim();
        if (query.length < 3) return null;

        const adminFromQuery = (() => {
            // 輕量抽縣市區（後端仍會再補；此處避免 Unknown）
            const s = String(query || '').replace(/\s+/g, '');
            const map = { 台北市: '臺北市', 台中市: '臺中市', 台南市: '臺南市', 台東縣: '臺東縣' };
            const counties = [
                '臺北市', '台北市', '新北市', '桃園市', '臺中市', '台中市', '臺南市', '台南市',
                '高雄市', '基隆市', '新竹市', '嘉義市', '新竹縣', '苗栗縣', '彰化縣', '南投縣',
                '雲林縣', '嘉義縣', '屏東縣', '宜蘭縣', '花蓮縣', '臺東縣', '台東縣', '澎湖縣',
                '金門縣', '連江縣'
            ];
            for (const c of counties) {
                if (!s.startsWith(c)) continue;
                const city = map[c] || c;
                const rest = s.slice(c.length);
                const dm = rest.match(/^([\u4e00-\u9fff]{1,4}[區鄉鎮市])/);
                return { city, district: dm ? dm[1] : '' };
            }
            return { city: '', district: '' };
        })();

        const tryParseResults = (data) => {
            const list = data?.results || data?.AddressList || (Array.isArray(data) ? data : []);
            for (const row of list) {
                const lat = Number(row.lat ?? row.Y ?? row.y);
                const lng = Number(row.lng ?? row.lon ?? row.X ?? row.x);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                if (Math.abs(lat) < 1 || Math.abs(lng) < 1) continue;
                return {
                    lat,
                    lng,
                    name: row.name || row.FullAddress || row.Address || query,
                    city: row.city || row.CountyName || adminFromQuery.city || '',
                    district: row.district || row.TownName || adminFromQuery.district || '',
                    h_aid_11: row.h_aid_11 || row.hAid || null
                };
            }
            return null;
        };

        try {
            const res = await fetch(`/api/proxy/tgos/geocode?q=${encodeURIComponent(query)}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const hit = tryParseResults(await res.json());
                if (hit) return hit;
            }
        } catch {
            /* continue */
        }

        try {
            const res = await fetch(`/api/proxy/geocode?q=${encodeURIComponent(query)}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const hit = tryParseResults(await res.json());
                if (hit) return hit;
            }
        } catch {
            /* continue */
        }
        return null;
    }

    async requestIsochrone(lat, lng) {
        this.setStatus('📡 正在獲取時間三圈＋兩公里步行距離圈…');
        this.armEmergencyWaitHint();
        if (this.bus) this.bus.emit(SovereignEvents?.LOADING_START || 'loading', true);
        try {
            const searchAddr = String(this.shadowRoot?.getElementById('isochroneSearch')?.value || '').trim();
            const selected = window.appState?.lastSelectedTarget || '';
            const address = searchAddr || selected || '';
            const payload = {
                lat, lng,
                city: window.appState?.currentCity || 'Unknown',
                district: window.appState?.currentDistrict || 'Unknown',
                address,
                h_aid_11: window.appState?.lastSelectedHAid || null,
                h_community_id: window.appState?.lastCommunityId || null,
                targetName: address || `Manual_${Date.now()}`,
                type: 'marker'
            };

            const [omniSettled, distSettled] = await Promise.allSettled([
                this.fetchIsoJson('/api/isochrones/generate-omni', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }),
                this.fetchIsoJson('/api/isochrones/ensure-walk-distance', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        lat,
                        lng,
                        address: payload.address,
                        city: payload.city === 'Unknown' ? '' : payload.city,
                        district: payload.district === 'Unknown' ? '' : payload.district,
                        h_aid_11: payload.h_aid_11,
                        h_community_id: payload.h_community_id,
                        // 搜尋框文字也當社區名（免一定要點建議列才有 lastCommunityName）
                        communityName: window.appState?.lastCommunityName || searchAddr || null,
                        targetName: payload.targetName
                    })
                })
            ]);

            if (omniSettled.status !== 'fulfilled') {
                throw omniSettled.reason || new Error('generate-omni 失敗');
            }
            const result = omniSettled.value;
            if (!result.success && result.errors?.length && !Object.keys(result.results || {}).length) {
                throw new Error(result.errors.join('; '));
            }
            if (result.h_aid_11) {
                window.appState = window.appState || {};
                window.appState.lastSelectedHAid = result.h_aid_11;
            }
            if (result.city && result.city !== 'Unknown') {
                window.appState = window.appState || {};
                window.appState.currentCity = result.city;
            }
            if (result.district && result.district !== 'Unknown') {
                window.appState = window.appState || {};
                window.appState.currentDistrict = result.district;
            }

            this.omniResults = { ...(result.results || {}) };
            this.shareHashByMode = {};
            this.shareHash = null;
            const pathBag = { ...(result.savedPaths || {}) };
            if (result.savedFoot && !pathBag['foot-walking']) {
                pathBag['foot-walking'] = result.savedFoot;
            }
            let distNote = '';
            const backupModeIds = [...(result.backupModes || [])];
            if (distSettled.status === 'fulfilled' && distSettled.value?.geojson) {
                this.omniResults['foot-distance-2km'] = distSettled.value.geojson;
                if (distSettled.value.h_aid_11) {
                    window.appState = window.appState || {};
                    window.appState.lastSelectedHAid = distSettled.value.h_aid_11;
                }
                if (distSettled.value.savedPath) {
                    pathBag['foot-distance-2km'] = distSettled.value.savedPath;
                } else if (distSettled.value.savedPaths?.['foot-distance-2km']) {
                    pathBag['foot-distance-2km'] = distSettled.value.savedPaths['foot-distance-2km'];
                }
                if (distSettled.value.orsEndpoint === 'backup') {
                    backupModeIds.push('foot-distance-2km');
                    distNote = ' · 兩公里（應急 ORS）';
                } else {
                    distNote = ` · 兩公里（${distSettled.value.source || 'ok'}）`;
                }
            } else {
                const errMsg = distSettled.status === 'rejected'
                    ? (distSettled.reason?.message || '距離圈失敗')
                    : (distSettled.value?.error || '無門牌碼');
                distNote = ` · 兩公里灰框（${errMsg}）`;
            }

            const mode = this.omniResults[this.activeTravelMode]
                ? this.activeTravelMode
                : (this.omniResults['foot-walking'] ? 'foot-walking' : Object.keys(this.omniResults)[0]);
            if (!mode || !this.omniResults[mode]) throw new Error('ORS 未回傳分時圈多邊形');
            this.activeTravelMode = mode;
            this.applyModeSharePaths(pathBag, { preferMode: mode });

            const geo = this.omniResults[mode];
            this.currentData = geo;
            this.fillTimeRangeOptions(mode);
            const maxVal = parseInt(
                this.shadowRoot.getElementById('timeRangeScale')?.value
                || String(this.TRAVEL_MODES[mode]?.defaultMax || 720),
                10
            );
            this.renderIsochroneLayers(geo, maxVal);
            this.syncTravelModeButtons();

            const successModes = Object.keys(this.omniResults).length;
            const errorCount = (result.errors || []).length;
            const label = result.identity?.label || payload.address || '未知名點';
            const modeHint = result.market?.marketType === 'comm-walk'
                ? `社區 ${result.market.communityName || ''}`.trim()
                : '公寓模式（社區門牌庫未命中）';
            const modeLabel = this.TRAVEL_MODES[mode]?.label || mode;
            const uniqueBackup = [...new Set(backupModeIds)];
            const backupLabels = this.formatBackupModeLabels(uniqueBackup);
            const diagBits = [result.diagId, distSettled.status === 'fulfilled' ? distSettled.value?.diagId : distSettled.reason?.diagId]
                .filter(Boolean);
            const diagNote = diagBits.length ? ` · 診斷 ${diagBits.join('／')}` : '';
            this.clearEmergencyWaitTimer();
            if (backupLabels.length) {
                this.setStatus(
                    `🔴 應急 ORS 已產出：${backupLabels.join('／')}（主站失效；可能無人口統計）· 顯示${modeLabel} · ${label}${diagNote}`,
                    { tone: 'emergency' }
                );
            } else {
                // 兩個失敗常混在一句：步行時間圈 + 兩公里距離圈（灰鈕）
                const bits = [`就緒 ${successModes} 模式`];
                if (errorCount) bits.push(`時間圈 ${errorCount} 失敗`);
                if (distNote) bits.push(distNote.replace(/^ · /, ''));
                bits.push(`顯示${modeLabel}`, label, modeHint);
                if (diagNote) bits.push(diagNote.replace(/^ · /, ''));
                this.setStatus(`✅ ${bits.join(' · ')}`);
            }
        } catch (err) {
            this.clearEmergencyWaitTimer();
            this.setStatus(`❌ ORS 請求失敗: ${err.message}`);
        }
    }

    createLegendBox() {
        if (document.getElementById('isoLegendBox')) return;
        const box = document.createElement('div');
        box.id = 'isoLegendBox';
        box.style = `
            position: absolute; bottom: 80px; left: 10px;
            background: rgba(255,255,255,0.95); padding: 12px;
            border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.2);
            z-index: 1000; display: none; border: 1px solid #ddd;
            font-family: Inter, sans-serif; backdrop-filter: blur(4px);
        `;
        document.body.appendChild(box);

        // 監聽截圖事件以隱藏圖例
        this.bus.on(SovereignEvents.SCREENSHOT_START, () => {
            box.style.display = 'none';
        });
    }

    renderIsochroneLayers(geojson, maxSeconds) {
        if (!this.map || !geojson) return;
        this.clearLayers();

        this.isoLayerGroup = L.layerGroup().addTo(this.map);
        // [v17.5.5] 向下兼容：同步至全域供 Legacy UI (如 Screenshot) 使用
        window.isochroneLayerGroup = this.isoLayerGroup;
        
        // fitBounds 外圈；勿只 panTo
        const polys = geojson.features.filter(f => 
            f.geometry.type === 'Polygon' && 
            Number(f.properties.value) <= maxSeconds
        ).sort((a, b) => Number(b.properties.value) - Number(a.properties.value));

        const baseOp = 0.05, maxOp = 0.2, step = polys.length > 1 ? (maxOp - baseOp) / (polys.length - 1) : 0;

        polys.forEach((poly, idx) => {
            const sec = Number(poly.properties.value);
            const styleObj = this.STYLE_CONFIG[sec] || { color: '#3388ff' };
            const op = baseOp + (idx * step);

            L.geoJSON(poly, {
                style: {
                    color: styleObj.color,
                    fillColor: styleObj.color,
                    weight: 3.5,
                    opacity: 0.9,
                    fillOpacity: op
                },
                _originalOpacity: op
            }).addTo(this.isoLayerGroup);
        });

        // 只廣播一次；不觸發公車／停車場自動開層
        if (polys.length) {
            const outer = polys[0];
            this.bus.emit(SovereignEvents.ISOC_RENDERED, {
                polygon: outer,
                value: Number(outer.properties?.value),
                allPolygons: polys
            });
            try {
                const b = L.geoJSON(outer).getBounds();
                if (b.isValid()) {
                    this.map.fitBounds(b, { padding: [48, 48], maxZoom: 16, animate: true });
                }
            } catch {
                /* ignore */
            }
        }

        this.updateLegend(polys);
        if (this.busOverlayOn) {
            this.showBusOverlay().catch((err) => {
                this.setStatus(`❌ 公車路線失敗: ${err.message || err}`);
            });
        }
    }

    updateLegend(polys) {
        const box = document.getElementById('isoLegendBox');
        if (!box) return;
        if (!polys || polys.length === 0) { box.style.display = 'none'; return; }

        const dist = this.isDistanceMode();
        const title = dist ? '📏 兩公里步行圈' : '⏱️ 分時圈圖例';
        let html = `<div style="font-weight:700;margin-bottom:8px;font-size:13px;">${title}</div>`;
        const sorted = [...polys].sort((a,b) => Number(a.properties.value) - Number(b.properties.value));
        sorted.forEach(p => {
            const v = Number(p.properties.value);
            const color = this.STYLE_CONFIG[v]?.color || '#3388ff';
            const label = dist ? `${v} m` : `${v / 60} min`;
            html += `
                <div style="display:flex;align-items:center;gap:10px;margin-bottom:5px;">
                    <span style="width:14px;height:14px;background:${color};border-radius:3px;border:1px solid #666;"></span>
                    <span style="font-size:12px;color:#333;">${label}</span>
                </div>`;
        });
        box.innerHTML = html;
        box.style.display = 'block';
    }

    /**
     * 畫面上／匯出圖：公車線只准用「首次收割分鐘」的 STYLE_CONFIG 色，禁止雜湊路線色
     */
    resolveMinuteColorForRoute(routeProps, assignments = []) {
        const p = routeProps || {};
        for (const a of assignments) {
            if (busRouteDetailsMatch(a.detail || {}, p)) return a.color;
        }
        return null;
    }

    busListOnPhone() {
        return window.matchMedia('(max-width: 760px)').matches;
    }

    syncBusButton() {
        const btn = this.shadowRoot?.getElementById('btnExport');
        if (!btn) return;
        btn.classList.toggle('is-on', this.busOverlayOn);
        btn.textContent = this.busOverlayOn ? '🚌 關閉公車路線' : '🚌 公車路線';
    }

    async toggleBusOverlay() {
        if (this.busOverlayOn) {
            this.busOverlayOn = false;
            this._busOverlayToken += 1;
            this.clearBusRoutes();
            this.hideBusListPanel();
            this.syncBusButton();
            this.setStatus('公車路線已關閉');
            return;
        }
        const polys = this.getActivePolygons();
        if (!polys.length) {
            this.setStatus('⚠️ 請先繪製分時圈');
            return;
        }
        this.busOverlayOn = true;
        this.syncBusButton();
        await this.showBusOverlay();
    }

    clearBusRoutes() {
        if (this.routeLayerGroup && this.map) this.map.removeLayer(this.routeLayerGroup);
        this.routeLayerGroup = null;
        this.routeLayersByKey = new Map();
        window.isochroneRouteLayers = [];
    }

    hideBusListPanel() {
        document.getElementById('isoBusListPanel')?.remove();
    }

    /**
     * 活地圖：分時圈顏色畫碰到外圈的公車線，並開可拖曳清單。預設關閉。
     * 窄螢幕不開清單，避免和地圖手勢搶。截圖匯出仍走 exportSnapshot。
     */
    async showBusOverlay() {
        const token = ++this._busOverlayToken;
        const polys = this.getActivePolygons();
        const Lref = leaflet();
        if (!this.busOverlayOn || !this.map || !Lref || !polys.length) return;
        if (typeof turf === 'undefined') {
            this.setStatus('❌ 缺少 Turf，無法畫公車路線');
            return;
        }

        this.setStatus('🚌 正在收割圈內公車…');
        const harvest = await this.collectIsochroneBusHarvest(polys);
        if (!this.busOverlayOn || token !== this._busOverlayToken) return;

        const outer = [...polys].sort(
            (a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0)
        )[0];
        const isoBounds = Lref.geoJSON(outer).getBounds();
        this.clearBusRoutes();
        if (!this.map.getPane('isochroneRoutePane')) {
            this.map.createPane('isochroneRoutePane');
            this.map.getPane('isochroneRoutePane').style.zIndex = 550;
            this.map.getPane('isochroneRoutePane').style.pointerEvents = 'auto';
        }
        this.routeLayerGroup = Lref.layerGroup([], { pane: 'isochroneRoutePane' }).addTo(this.map);
        window.isochroneRouteLayers = [this.routeLayerGroup];

        const drawnKeys = new Set();
        for (const a of harvest.routeAssignments || []) {
            if (!a?.color || drawnKeys.has(a.key)) continue;
            const feat = (harvest.routeFeatures || []).find((route) =>
                busRouteDetailsMatch(a.detail || {}, route.properties || {})
            );
            if (!feat) continue;
            const toDraw = filterRouteFeatureToIsochroneParts(feat, outer, turf);
            if (!toDraw?.geometry) continue;
            drawnKeys.add(a.key);
            const label = String(a.label || '公車');
            const minutes = a.minutes;
            Lref.geoJSON(toDraw, {
                pane: 'isochroneRoutePane',
                interactive: true,
                style: {
                    color: a.color,
                    weight: Number(a.seconds) >= 600 ? 5.5 : 4,
                    opacity: 0.92,
                    dashArray: null,
                    lineJoin: 'round',
                    lineCap: 'round'
                },
                onEachFeature: (_f, layer) => {
                    this.routeLayersByKey.set(a.key, layer);
                    layer.bindPopup(
                        `<div style="font:600 13px/1.4 sans-serif">${this.escapeListCell(label)}</div>`
                        + `<div style="font:12px/1.3 sans-serif;color:#64748b">${this.escapeListCell(minutes)} 分鐘</div>`
                    );
                    layer.on('click', (e) => {
                        Lref.DomEvent.stopPropagation(e);
                        layer.openPopup(e.latlng);
                        this.markBusPill(a.key);
                    });
                }
            }).addTo(this.routeLayerGroup);
        }

        for (const stop of harvest.allStops || []) {
            const ll = stopLonLat(stop);
            if (!ll || !isoBounds.contains([ll[1], ll[0]])) continue;
            const name = stop.properties?.name
                || stop.properties?.StopName
                || stop.properties?.stop_name
                || '公車站';
            const marker = Lref.circleMarker([ll[1], ll[0]], {
                pane: 'isochroneRoutePane',
                interactive: true,
                radius: 5,
                color: '#111',
                weight: 1.5,
                fillColor: '#fff',
                fillOpacity: 1
            }).addTo(this.routeLayerGroup);
            marker.bindPopup(`<div style="font:600 13px/1.4 sans-serif">${this.escapeListCell(name)}</div>`);
            marker.on('click', (e) => {
                Lref.DomEvent.stopPropagation(e);
                marker.openPopup();
            });
        }

        if (harvest.rings?.length && drawnKeys.size) {
            for (const ring of harvest.rings) {
                ring.routes = (ring.routes || []).filter((r) => drawnKeys.has(r.key));
            }
        }
        if (this.busListOnPhone()) {
            this.hideBusListPanel();
            this.setStatus(`✅ 公車路線已顯示（手機不開清單）· 線 ${drawnKeys.size}`);
            return;
        }
        this.renderBusListPanel(harvest.rings || []);
        this.setStatus(`✅ 公車路線已顯示 · 線 ${drawnKeys.size}`);
    }

    markBusPill(key) {
        const panel = document.getElementById('isoBusListPanel');
        if (!panel) return;
        panel.querySelectorAll('[data-route-key]').forEach((el) => {
            el.style.outline = el.getAttribute('data-route-key') === String(key)
                ? '2px solid #111'
                : '';
        });
        panel.querySelector(`[data-route-key="${CSS.escape(String(key))}"]`)?.scrollIntoView({ block: 'nearest' });
    }

    renderBusListPanel(rings) {
        this.hideBusListPanel();
        const panel = document.createElement('div');
        panel.id = 'isoBusListPanel';
        panel.style.cssText = [
            'position:fixed', 'top:88px', 'right:16px', 'z-index:1200',
            'width:min(360px, calc(100vw - 24px))', 'max-height:70vh',
            'background:#fff', 'color:#111', 'border-radius:12px',
            'box-shadow:0 8px 28px rgba(0,0,0,.28)', 'display:flex',
            'flex-direction:column', 'overflow:hidden', 'font-family:sans-serif'
        ].join(';');

        const head = document.createElement('div');
        head.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 10px;cursor:grab;background:#1b2430;color:#fff;user-select:none;';
        const title = document.createElement('div');
        title.textContent = '公車清單';
        title.style.cssText = 'flex:1;font-weight:700;font-size:14px;';
        const close = document.createElement('button');
        close.type = 'button';
        close.textContent = '×';
        close.style.cssText = 'border:0;background:transparent;color:#fff;font-size:20px;cursor:pointer;line-height:1;';
        close.addEventListener('click', () => {
            this.toggleBusOverlay().catch(() => {});
        });
        head.appendChild(title);
        head.appendChild(close);
        panel.appendChild(head);

        const body = document.createElement('div');
        body.style.cssText = 'overflow:auto;padding:8px 10px 12px;';
        const bySec = new Map((rings || []).map((r) => [Number(r.seconds), r]));
        for (const sec of ISOCHRONE_BUS_RING_SECONDS) {
            const hit = bySec.get(sec);
            const color = this.STYLE_CONFIG[sec]?.color || hit?.color || '#64748b';
            const row = document.createElement('div');
            row.style.cssText = 'display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-bottom:1px dashed #e2e8f0;';
            const label = document.createElement('div');
            label.textContent = `${sec / 60}分鐘`;
            label.style.cssText = `flex:0 0 52px;text-align:right;font-weight:800;color:${color};font-size:13px;`;
            const pills = document.createElement('div');
            pills.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;flex:1;';
            const seen = new Set();
            for (const route of hit?.routes || []) {
                const lab = String(route.label || '').trim();
                const labKey = lab.toLowerCase().replace(/\s+/g, '');
                if (!lab || seen.has(labKey)) continue;
                seen.add(labKey);
                const pill = document.createElement('button');
                pill.type = 'button';
                pill.textContent = lab;
                pill.setAttribute('data-route-key', String(route.key));
                const fg = pillTextColor(color);
                pill.style.cssText = [
                    `background:${color}`, `color:${fg}`, 'border:0', 'border-radius:999px',
                    'padding:4px 10px', 'font-weight:700', 'font-size:12px', 'cursor:pointer'
                ].join(';');
                pill.addEventListener('click', () => {
                    const layer = this.routeLayersByKey.get(route.key);
                    this.markBusPill(route.key);
                    if (!layer || !this.map) return;
                    const b = layer.getBounds?.();
                    if (b?.isValid?.()) this.map.fitBounds(b, { padding: [40, 40], maxZoom: 16, animate: true });
                    layer.openPopup?.();
                });
                pills.appendChild(pill);
            }
            if (!seen.size) {
                const empty = document.createElement('span');
                empty.textContent = '—';
                empty.style.color = '#94a3b8';
                pills.appendChild(empty);
            }
            row.appendChild(label);
            row.appendChild(pills);
            body.appendChild(row);
        }
        panel.appendChild(body);
        document.body.appendChild(panel);

        let drag = null;
        head.addEventListener('pointerdown', (e) => {
            if (e.target === close) return;
            drag = { dx: e.clientX - panel.offsetLeft, dy: e.clientY - panel.offsetTop };
            head.setPointerCapture(e.pointerId);
        });
        head.addEventListener('pointermove', (e) => {
            if (!drag) return;
            panel.style.left = `${Math.max(8, e.clientX - drag.dx)}px`;
            panel.style.top = `${Math.max(8, e.clientY - drag.dy)}px`;
            panel.style.right = 'auto';
        });
        head.addEventListener('pointerup', () => { drag = null; });
        head.addEventListener('pointercancel', () => { drag = null; });
    }

    toggleFill() {
        if (!this.isoLayerGroup) return;
        let isHollow = false;
        this.isoLayerGroup.eachLayer((l) => l.eachLayer((sub) => {
            if (sub.options.fillOpacity === 0) isHollow = true;
        }));
        this.isoLayerGroup.eachLayer((l) => {
            const originalOp = l.options._originalOpacity || 0.2;
            l.eachLayer((sub) => sub.setStyle({ fillOpacity: isHollow ? originalOp : 0 }));
        });
    }

    /**
     * 分時圈 × 公車站牌：由內（2 min）向外逐圈抓站，站／路線皆不重複
     */
    async collectIsochroneBusHarvest(polys) {
        if (!polys?.length || typeof turf === 'undefined') {
            return { rings: [], allStops: [], routeAssignments: [], routeFeatures: [] };
        }

        const busCabinet = window.appCore?.getCabinet('bus');
        if (!busCabinet) {
            return { rings: [], allStops: [], routeAssignments: [], routeFeatures: [] };
        }

        if (typeof busCabinet.loadBusRouteIndex === 'function' && !busCabinet.busRouteIndex) {
            await busCabinet.loadBusRouteIndex();
        }

        const Lref = leaflet();
        const outer = [...polys].sort(
            (a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0)
        )[0];
        const outerBounds = Lref?.geoJSON(outer).getBounds?.();
        let stops = [];
        if (outerBounds && typeof busCabinet.fetchStopsForBounds === 'function') {
            try {
                const data = await busCabinet.fetchStopsForBounds(outerBounds);
                stops = data?.features || [];
            } catch (err) {
                console.warn('[IsoExport] stops bbox failed:', err.message);
            }
        }
        if (!stops.length && busCabinet.busData?.stops?.length) {
            stops = busCabinet.busData.stops;
        }

        const bySec = new Map();
        for (const f of polys) {
            const sec = Number(f.properties?.value);
            if (!Number.isFinite(sec)) continue;
            bySec.set(sec, f);
        }
        const maxSec = Math.max(...polys.map((f) => Number(f.properties?.value) || 0));
        const ringsAsc = ISOCHRONE_BUS_RING_SECONDS
            .filter((sec) => bySec.has(sec) && sec <= maxSec)
            .map((sec) => ({
                seconds: sec,
                minutes: sec / 60,
                color: this.STYLE_CONFIG[sec]?.color || '#64748b',
                feature: bySec.get(sec)
            }));

        // 若下拉只開到某分鐘，仍用實際有的圈；非標準秒數也併入
        for (const f of polys) {
            const sec = Number(f.properties?.value);
            if (!Number.isFinite(sec) || ringsAsc.some((r) => r.seconds === sec)) continue;
            ringsAsc.push({
                seconds: sec,
                minutes: Math.round(sec / 60),
                color: this.STYLE_CONFIG[sec]?.color || '#64748b',
                feature: f
            });
        }
        ringsAsc.sort((a, b) => a.seconds - b.seconds);

        const harvested = harvestBusStopsByIsochroneRings({
            ringsAsc,
            stops,
            pointInPolygon: (lon, lat, polygonFeature) => {
                try {
                    return turf.booleanPointInPolygon(turf.point([lon, lat]), polygonFeature);
                } catch {
                    return false;
                }
            },
            routesForStop: (stop) => {
                if (typeof busCabinet.rawRouteDetailsForStop === 'function') {
                    return busCabinet.rawRouteDetailsForStop(stop) || [];
                }
                return stop?.properties?.h_route_details || [];
            }
        });

        const routeFeatures = await this.fetchRouteFeaturesForAssignments(harvested.routeAssignments);
        return { ...harvested, routeFeatures };
    }

    /**
     * 報表／高亮：依收割路線拉完整幾何。
     * 單次 by-ids 上限 120（URL／後端）；路線多時（外圈紫線）必須分批，禁止 .slice 截掉後段。
     */
    async fetchRouteFeaturesForAssignments(assignments) {
        const ids = [...new Set(
            (assignments || []).flatMap((a) => busRouteFetchIds(a.detail || {}))
        )];
        if (!ids.length) {
            console.warn('[IsoExport] no strong route ids for by-ids (blocked bare ref collision)');
            return [];
        }

        const CHUNK = 80;
        const merged = [];
        const seenFeat = new Set();
        try {
            for (let i = 0; i < ids.length; i += CHUNK) {
                const chunk = ids.slice(i, i + CHUNK);
                // 報表要完整路網：強制 full 層（zoom≥16），禁止 z14 抽稀
                const q = new URLSearchParams({
                    ids: chunk.join(','),
                    zoom: '18',
                    client: 'desktop',
                    variants: '1',
                    band: 'full'
                });
                const res = await fetch(`/api/live/bus/routes/by-ids?${q}`, { credentials: 'include' });
                if (!res.ok) {
                    console.warn(`[IsoExport] by-ids chunk HTTP ${res.status} @${i}`);
                    continue;
                }
                const data = await res.json();
                for (const f of data.features || []) {
                    const p = f.properties || {};
                    const key = String(
                        p.h_route_key || p.h_uid || p.route_id || p.id || JSON.stringify(p).slice(0, 80)
                    );
                    if (seenFeat.has(key)) continue;
                    seenFeat.add(key);
                    merged.push(f);
                }
            }
            if (merged.length < (assignments || []).length) {
                console.warn(
                    `[IsoExport] route geometry partial: features=${merged.length} assignments=${assignments.length} ids=${ids.length}`
                );
            }
            return merged;
        } catch (err) {
            console.warn('[IsoExport] routes by-ids failed:', err.message);
            return merged;
        }
    }

    /**
     * 公車報表雙圖：① 站牌／路線地圖 ② 分鐘 pill 清單卡
     * 清單尺寸依 A4 內文可讀比例（190×95 mm），非整頁輸出。
     */
    async exportSnapshot() {
        this.setStatus('📸 正在由內向外收割公車站牌與報表…');

        const Lref = leaflet();
        if (!this.map || !Lref) {
            this.setStatus('❌ 地圖尚未就緒');
            return;
        }
        if (!window.html2canvas) {
            this.setStatus('❌ html2canvas 未載入');
            return;
        }
        if (typeof turf === 'undefined') {
            this.setStatus('❌ 缺少 Turf，無法判斷站牌是否在圈內');
            return;
        }

        const polys = this.getActivePolygons();
        if (!polys.length) {
            this.setStatus('⚠️ 請先繪製分時圈');
            return;
        }

        const dateStr = new Date().toISOString().slice(0, 10);
        const harvest = await this.collectIsochroneBusHarvest(polys);
        const stopCount = harvest.allStops?.length || 0;
        const routeCount = harvest.routeAssignments?.length || 0;

        let mapOk = false;
        try {
            await this.exportBusRoutesMapPng(polys, harvest, dateStr);
            mapOk = true;
        } catch (err) {
            console.error('[IsoExport] Bus routes map failed:', err);
            this.setStatus(`❌ 公車路線圖失敗: ${err.message}`);
        }

        await new Promise((r) => setTimeout(r, 500));

        try {
            await this.exportRouteListPng(harvest.rings || [], dateStr);
        } catch (err) {
            console.error('[IsoExport] Bus route list failed:', err);
            if (mapOk) {
                this.setStatus(`⚠️ 路線圖已儲存，清單失敗: ${err.message}`);
            } else {
                this.setStatus(`❌ 清單失敗: ${err.message}`);
            }
            return;
        }

        if (mapOk) {
            const drawn = harvest.routeAssignments?.length || 0;
            this.setStatus(`✅ 公車報表 2 張：站 ${stopCount}／線 ${drawn}（收割 ${routeCount}）`);
        }
    }

    /**
     * 分時人口報表 Double-Export：① 動態尺寸地圖（淺色交通底圖）② 固定比例人口表
     * 地圖優先用專用 ghost（可靠）；live capture 僅備援。兩張下載間隔防瀏覽器擋第二張。
     */
    async exportPopulationReport(opts = {}) {
        this.setStatus('📸 正在生成分時人口地圖與人口表…');

        if (!this.map) {
            this.setStatus('❌ 地圖尚未就緒');
            return;
        }
        if (!window.html2canvas) {
            this.setStatus('❌ html2canvas 未載入');
            return;
        }

        const polys = this.getActivePolygons();
        if (!polys.length) {
            this.setStatus('⚠️ 請先繪製分時圈');
            return;
        }

        const dateStr = new Date().toISOString().slice(0, 10);
        const popRows = this.collectPopulationRows(polys);
        const missingPop = popRows.some((r) => r.population == null);

        let mapOk = false;
        let mapErr = '';
        try {
            // 專用 ghost 較穩定；不依賴 live 複製（易破圖／白格／無下載）
            await this.exportPopulationMapPng(polys, dateStr);
            mapOk = true;
        } catch (err) {
            console.error('[IsoExport] Population map (dedicated) failed:', err);
            mapErr = err.message || String(err);
            if (typeof opts.captureMap === 'function') {
                try {
                    const canvas = await opts.captureMap();
                    downloadCanvasPng(canvas, `分時人口_地圖_${dateStr}.png`);
                    mapOk = true;
                    mapErr = '';
                } catch (err2) {
                    console.error('[IsoExport] Population map (live ghost) failed:', err2);
                    mapErr = err2.message || mapErr;
                }
            }
        }

        // 雙檔下載：間隔避免瀏覽器只放行其中一張
        await new Promise((r) => setTimeout(r, 500));

        try {
            await this.exportPopulationTablePng(popRows, dateStr);
        } catch (err) {
            console.error('[IsoExport] Population table failed:', err);
            if (mapOk) {
                this.setStatus(`⚠️ 地圖已儲存，人口表失敗: ${err.message}`);
            } else {
                this.setStatus(`❌ 人口表失敗: ${err.message}`);
            }
            return;
        }

        if (mapOk) {
            const hint = missingPop
                ? '（部分圈無人口：請重繪分時圈以取得 ORS total_pop）'
                : '';
            this.setStatus(`✅ 已儲存 2 張：分時人口地圖 + 人口表${hint}`);
        } else {
            this.setStatus(`⚠️ 僅人口表已儲存；地圖失敗: ${mapErr || '未知'}`);
        }
    }

    collectPopulationRows(polys) {
        const bySec = new Map();
        for (const f of polys || []) {
            const sec = Number(f.properties?.value);
            if (!Number.isFinite(sec) || sec <= 0) continue;
            const raw = f.properties?.total_pop ?? f.properties?.total_population;
            const pop = Number(raw);
            bySec.set(sec, {
                seconds: sec,
                minutes: Math.round(sec / 60),
                population: Number.isFinite(pop) ? Math.round(pop) : null
            });
        }
        // 標準 2–12 分鐘欄位（缺圈仍留空格以對齊報表）
        const slots = [120, 240, 360, 480, 600, 720];
        return slots.map((sec) => bySec.get(sec) || {
            seconds: sec,
            minutes: sec / 60,
            population: null
        });
    }

    /** 圖一備援：分時圈＋動態 viewport＋淺色交通底圖（無 live ghost 時） */
    async exportPopulationMapPng(polys, dateStr) {
        const Lref = leaflet();
        const outer = [...polys].sort(
            (a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0)
        )[0];
        const isoBounds = Lref.geoJSON(outer).getBounds();
        if (!isoBounds.isValid()) throw new Error('分時圈邊界無效');

        // 版面 96dpi；輸出用 PRINT_SCALE 放大（與公車報表同一座標系）
        const viewport = computeIsoDynamicViewport(isoBounds, this.map);
        const width = viewport.valid ? viewport.width : 900;
        const height = viewport.valid ? viewport.height : 600;

        const ghostEl = document.createElement('div');
        ghostEl.style.cssText = [
            `width:${width}px`, `height:${height}px`,
            'position:fixed', 'left:0', 'top:0', 'z-index:-9999', 'visibility:visible'
        ].join(';');
        document.body.appendChild(ghostEl);

        let ghostMap = null;
        try {
            ghostMap = Lref.map(ghostEl, {
                zoomControl: false,
                attributionControl: false,
                fadeAnimation: false,
                zoomAnimation: false,
                preferCanvas: false
            });
            Lref.tileLayer(LIGHT_TRANSPORT_TILE_URL, {
                maxZoom: 19,
                crossOrigin: true
            }).addTo(ghostMap);
            ghostMap.invalidateSize(true);
            ghostMap.fitBounds(isoBounds, { padding: [28, 28], animate: false, maxZoom: 16 });
            ghostMap.invalidateSize(true);
            await this.injectDistrictBoundaries(ghostMap, Lref, isoBounds);

            [...polys].sort((a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0))
                .forEach((poly) => {
                    const sec = Number(poly.properties?.value);
                    const styleObj = this.STYLE_CONFIG[sec] || { color: '#666', dash: null };
                    Lref.geoJSON(poly, {
                        style: {
                            color: styleObj.color,
                            fillColor: styleObj.color,
                            weight: 2.5,
                            opacity: 0.95,
                            fillOpacity: 0,
                            dashArray: null
                        }
                    }).addTo(ghostMap);
                });

            // 預設載入圈內公車站圓點（不畫路線，免干擾人口底圖）
            try {
                if (typeof turf !== 'undefined') {
                    const harvest = await this.collectIsochroneBusHarvest(polys);
                    for (const stop of harvest.allStops || []) {
                        const ll = stopLonLat(stop);
                        if (!ll) continue;
                        if (!isoBounds.contains([ll[1], ll[0]])) continue;
                        Lref.circleMarker([ll[1], ll[0]], {
                            radius: 5,
                            color: '#c62828',
                            weight: 1.5,
                            fillColor: '#fff',
                            fillOpacity: 1
                        }).addTo(ghostMap);
                    }
                }
            } catch (stopErr) {
                console.warn('[IsoExport] population map bus stops skipped:', stopErr.message);
            }

            await waitForMapTiles(ghostMap, 12000);
            await waitForPaintFrames(6);
            const canvas = await captureMapContainer(ghostEl, {
                includeHtmlMarkers: false,
                devicePixelRatio: SCREENSHOT_PRINT_SCALE
            });
            downloadCanvasPng(canvas, `分時人口_地圖_${dateStr}.png`);
        } finally {
            if (ghostMap) ghostMap.remove();
            ghostEl.remove();
        }
    }

    /**
     * 圖二：圈內人口表 — 版面按 96dpi 排字，html2canvas 再放大至 300dpi（避免 13px 米雕）
     * 總框固定（POP_TABLE_CM）；分鐘欄寬依人口／表頭字寬比例分配，避免萬／十萬位換行截斷。
     */
    async exportPopulationTablePng(rows, dateStr) {
        await ensureScreenshotFontsLoaded();
        const layout = formatPixelSize(SCREENSHOT_FORMATS.POP_TABLE_CM, SCREENSHOT_LAYOUT_DPI);
        const width = layout.width;
        const height = layout.height;
        const S = POP_TABLE_HEADER_STYLE;
        let fontPx = Math.max(15, Math.round(height * 0.22));

        const popTexts = rows.map((r) => (r.population != null
            ? `${r.population.toLocaleString('zh-Hant-TW')}人`
            : '—'));
        const headerTexts = rows.map((r) => `${r.minutes}分鐘`);

        const measureWithFont = (px) => {
            try {
                const c = document.createElement('canvas');
                const ctx = c.getContext('2d');
                if (!ctx) return null;
                ctx.font = `800 ${px}px ${POP_TABLE_FONT_STACK}`;
                return (s) => Math.max(1, ctx.measureText(String(s ?? '')).width);
            } catch {
                return null;
            }
        };

        let measure = measureWithFont(fontPx);
        let { labelPct, colPcts } = allocatePopTableColPercents({
            labelTexts: ['步行時間', '人口'],
            headers: headerTexts,
            bodies: popTexts,
            measure: measure || undefined
        });

        // 若最寬人口格仍擠（估）：略降字級再量一次，總框不變
        if (measure) {
            const avail = (Math.min(...colPcts) / 100) * width - 8;
            const widest = Math.max(...popTexts.map((t) => measure(t)));
            if (widest > avail && avail > 0) {
                fontPx = Math.max(12, Math.floor(fontPx * (avail / widest)));
                measure = measureWithFont(fontPx) || measure;
                ({ labelPct, colPcts } = allocatePopTableColPercents({
                    labelTexts: ['步行時間', '人口'],
                    headers: headerTexts,
                    bodies: popTexts,
                    measure
                }));
            }
        }

        const host = document.createElement('div');
        host.style.cssText = [
            'position:fixed', 'left:-9999px', 'top:0',
            `width:${width}px`, `height:${height}px`,
            'background:#fff', 'box-sizing:border-box',
            `font-family:${POP_TABLE_FONT_STACK}`,
            'font-weight:800',
            'padding:0', 'overflow:hidden', 'z-index:-1',
            'display:flex', 'align-items:stretch'
        ].join(';');

        const table = document.createElement('table');
        table.style.cssText = [
            'width:100%', 'height:100%', 'border-collapse:collapse',
            'table-layout:fixed', `font-size:${fontPx}px`, 'border:2px solid #000',
            'font-weight:800'
        ].join(';');

        const colgroup = document.createElement('colgroup');
        const labelCol = document.createElement('col');
        labelCol.style.width = `${labelPct}%`;
        colgroup.appendChild(labelCol);
        for (const pct of colPcts) {
            const col = document.createElement('col');
            col.style.width = `${pct}%`;
            colgroup.appendChild(col);
        }
        table.appendChild(colgroup);

        const head = document.createElement('tr');
        const labelTh = document.createElement('th');
        labelTh.textContent = '步行時間';
        labelTh.style.cssText = [
            'padding:4px 2px', `background:${S.labelBg}`,
            `border:1px solid ${S.border}`, 'font-weight:800',
            `color:${S.labelColor}`, 'text-align:center', 'vertical-align:middle',
            'white-space:nowrap', 'overflow:hidden'
        ].join(';');
        head.appendChild(labelTh);
        rows.forEach((r, i) => {
            const th = document.createElement('th');
            th.textContent = headerTexts[i];
            const isEarly = Number(r.minutes) <= 2;
            const textColor = S.minuteText[r.minutes] || '#ffffff';
            th.style.cssText = [
                'padding:4px 2px', `border:1px solid ${S.border}`, 'font-weight:800',
                `color:${textColor}`,
                `background:${isEarly ? S.earlyBg : S.lateBg}`,
                'text-align:center', 'vertical-align:middle',
                'white-space:nowrap', 'overflow:hidden'
            ].join(';');
            head.appendChild(th);
        });
        table.appendChild(head);

        const body = document.createElement('tr');
        const labelTd = document.createElement('td');
        labelTd.textContent = '人口';
        labelTd.style.cssText = [
            'padding:4px 2px', `background:${S.bodyBg}`, `border:1px solid ${S.border}`,
            'font-weight:800', 'text-align:center', 'vertical-align:middle', `color:${S.bodyColor}`,
            'white-space:nowrap', 'overflow:hidden'
        ].join(';');
        body.appendChild(labelTd);
        rows.forEach((r, i) => {
            const td = document.createElement('td');
            td.textContent = popTexts[i];
            td.style.cssText = [
                'padding:4px 2px', `border:1px solid ${S.border}`, 'text-align:center',
                'vertical-align:middle', `color:${S.bodyColor}`, 'font-weight:800',
                `background:${S.bodyBg}`,
                'white-space:nowrap', 'overflow:hidden'
            ].join(';');
            body.appendChild(td);
        });
        table.appendChild(body);
        host.appendChild(table);

        document.body.appendChild(host);
        try {
            const canvas = await html2canvas(host, {
                width,
                height,
                scale: SCREENSHOT_PRINT_SCALE,
                backgroundColor: '#ffffff',
                useCORS: false,
                allowTaint: false
            });
            downloadCanvasPng(canvas, `分時人口_人口表_${dateStr}.png`);
        } finally {
            host.remove();
        }
    }

    getActivePolygons() {
        const maxSec = parseInt(this.shadowRoot?.getElementById('timeRangeScale')?.value || '720', 10);
        if (this.currentData?.features?.length) {
            return this.currentData.features
                .filter((f) => f.geometry?.type === 'Polygon' && Number(f.properties?.value) <= maxSec)
                .sort((a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0));
        }

        if (!this.isoLayerGroup) return [];
        const polys = [];
        this.isoLayerGroup.eachLayer((layer) => {
            if (layer.feature?.geometry?.type === 'Polygon') {
                polys.push(layer.feature);
                return;
            }
            const gj = layer.toGeoJSON?.();
            const features = gj?.features || (gj?.type === 'Feature' ? [gj] : []);
            features.forEach((f) => {
                if (f.geometry?.type === 'Polygon') polys.push(f);
            });
        });
        return polys.sort((a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0));
    }

    /**
     * 圖一：分時圈＋圈內站牌＋這些公車實際走到的路。
     * 取景跟路線本身（車能到哪），不裁「圈外 N 公里」。
     * 只丟完全沒碰到最外圈的外縣碎片；碰到的折線整段保留，禁止 bboxClip。
     */
    async exportBusRoutesMapPng(polys, harvest, dateStr) {
        const Lref = leaflet();
        const outer = [...polys].sort(
            (a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0)
        )[0];
        const isoBounds = Lref.geoJSON(outer).getBounds();
        if (!isoBounds.isValid()) {
            throw new Error('分時圈邊界無效');
        }

        const routeFeatures = harvest?.routeFeatures || [];
        const allStops = harvest?.allStops || [];
        const turfApi = typeof turf !== 'undefined' ? turf : null;
        const assignments = harvest?.routeAssignments || [];

        const drawnRouteFeats = [];
        const drawnKeys = new Set();
        for (const a of assignments) {
            if (!a?.color || drawnKeys.has(a.key)) continue;
            const feat = routeFeatures.find((route) =>
                busRouteDetailsMatch(a.detail || {}, route.properties || {})
            );
            if (!feat) continue;
            const toDraw = filterRouteFeatureToIsochroneParts(feat, outer, turfApi);
            if (!toDraw?.geometry) continue;
            drawnKeys.add(a.key);
            drawnRouteFeats.push({ feat: toDraw, color: a.color, seconds: a.seconds });
        }

        // 清單與地圖對齊：丟掉跨縣毒桶／無幾何的 pill
        if (harvest?.rings?.length && drawnKeys.size) {
            for (const ring of harvest.rings) {
                ring.routes = (ring.routes || []).filter((r) => drawnKeys.has(r.key));
            }
            harvest.routeAssignments = (harvest.routeAssignments || []).filter((a) =>
                drawnKeys.has(a.key)
            );
        }

        const frameBounds =
            boundsFromGeoFeatures(
                [outer, ...drawnRouteFeats.map((d) => d.feat)],
                isoBounds
            ) || isoBounds;

        const viewport = computeIsoDynamicViewport(frameBounds, this.map, {
            maxDim: 1600,
            minZoom: 12,
            maxZoom: 17
        });
        const width = viewport.valid ? viewport.width : 900;
        const height = viewport.valid ? viewport.height : 600;

        const ghostEl = document.createElement('div');
        ghostEl.style.cssText = [
            `width:${width}px`, `height:${height}px`,
            'position:fixed', 'left:0', 'top:0', 'z-index:-9999', 'visibility:visible'
        ].join(';');
        document.body.appendChild(ghostEl);

        let ghostMap = null;
        try {
            ghostMap = Lref.map(ghostEl, {
                zoomControl: false,
                attributionControl: false,
                fadeAnimation: false,
                zoomAnimation: false,
                preferCanvas: false
            });

            const tileLayer = Lref.tileLayer(LIGHT_CARTO_TILE_URL, {
                maxZoom: 19,
                crossOrigin: true
            }).addTo(ghostMap);
            ghostMap.invalidateSize(true);
            ghostMap.fitBounds(frameBounds, { padding: [24, 24], animate: false, maxZoom: 17 });
            ghostMap.invalidateSize(true);

            await this.injectDistrictBoundaries(ghostMap, Lref, frameBounds, { opacity: 0.6 });

            [...polys]
                .sort((a, b) => Number(b.properties?.value || 0) - Number(a.properties?.value || 0))
                .forEach((poly) => {
                    const sec = Number(poly.properties?.value);
                    const styleObj = this.STYLE_CONFIG[sec] || { color: '#666', dash: null };
                    Lref.geoJSON(poly, {
                        style: {
                            color: styleObj.color,
                            fillColor: styleObj.color,
                            weight: 2.5,
                            opacity: 0.95,
                            fillOpacity: 0.16,
                            dashArray: styleObj.dash || null
                        }
                    }).addTo(ghostMap);
                });

            const sortedDraw = [...drawnRouteFeats].sort(
                (a, b) => (a.seconds || 0) - (b.seconds || 0)
            );
            for (const { feat, color, seconds } of sortedDraw) {
                const isOuter = Number(seconds) >= 600;
                Lref.geoJSON(feat, {
                    style: {
                        color,
                        weight: isOuter ? 5.5 : 4,
                        opacity: isOuter ? 0.95 : 0.88,
                        dashArray: null,
                        lineJoin: 'round',
                        lineCap: 'round'
                    }
                }).addTo(ghostMap);
            }

            for (const stop of allStops) {
                const ll = stopLonLat(stop);
                if (!ll) continue;
                if (!isoBounds.contains([ll[1], ll[0]])) continue;
                Lref.circleMarker([ll[1], ll[0]], {
                    radius: 5,
                    color: '#111',
                    weight: 1.5,
                    fillColor: '#fff',
                    fillOpacity: 1
                }).addTo(ghostMap);
            }

            ghostMap.fitBounds(frameBounds, { padding: [24, 24], animate: false, maxZoom: 17 });
            ghostMap.invalidateSize(true);

            await waitForMapTiles(ghostMap, 12000);
            await waitForPaintFrames(6);
            if (tileLayer?.isLoading?.()) {
                await waitForMapTiles(ghostMap, 6000);
            }
            await waitForPaintFrames(2);

            const canvas = await captureMapContainer(ghostEl, {
                includeHtmlMarkers: false,
                devicePixelRatio: SCREENSHOT_PRINT_SCALE
            });
            downloadCanvasPng(canvas, `分時圈_公車路線_${dateStr}.png`);
        } finally {
            if (ghostMap) ghostMap.remove();
            ghostEl.remove();
        }
    }

    /** 報表用行政區邊界 — 公車報表 opacity 0.6（60%） */
    async injectDistrictBoundaries(ghostMap, Lref, frameBounds, options = {}) {
        return injectDistrictBoundariesToGhost(ghostMap, frameBounds, options);
    }

    /**
     * 圖二：分鐘 pill 清單卡（獨立 PNG）
     * 寬＝A4 內文；高＝依各列 pill 換行動態夾在 minHMm～maxHMm（禁止等分 flex＋overflow 疊字）
     */
    async exportRouteListPng(rings, dateStr) {
        await ensureScreenshotFontsLoaded();
        const L = BUS_ROUTE_LIST_LAYOUT;
        const width = mmToPx(L.wMm, SCREENSHOT_LAYOUT_DPI);
        const pad = mmToPx(L.padMm, SCREENSHOT_LAYOUT_DPI);
        const rowPad = mmToPx(L.rowPadMm ?? 2.2, SCREENSHOT_LAYOUT_DPI);
        const minutePx = ptToPx(L.minutePt);
        const pillPx = ptToPx(L.pillPt);
        const labelW = Math.max(56, Math.round(minutePx * 4.4));

        const host = document.createElement('div');
        host.style.cssText = [
            'position:fixed', 'left:-9999px', 'top:0', 'z-index:-1',
            `width:${width}px`, 'height:auto',
            'background:#fff', 'box-sizing:border-box',
            `font-family:${POP_TABLE_FONT_STACK}`,
            'font-weight:800',
            `padding:${pad}px ${pad + 2}px`,
            `border:1px solid ${L.border}`, 'border-radius:12px',
            'display:flex', 'flex-direction:column', 'justify-content:flex-start', 'gap:0'
        ].join(';');

        const bySec = new Map((rings || []).map((r) => [Number(r.seconds), r]));
        const rows = ISOCHRONE_BUS_RING_SECONDS.map((sec) => {
            const hit = bySec.get(sec);
            const color = this.STYLE_CONFIG[sec]?.color || hit?.color || '#64748b';
            return {
                seconds: sec,
                minutes: sec / 60,
                color,
                routes: hit?.routes || []
            };
        });

        rows.forEach((ring, idx) => {
            const row = document.createElement('div');
            row.style.cssText = [
                'display:flex', 'align-items:flex-start', 'gap:0',
                'flex:0 0 auto',
                `padding:${rowPad}px 0`,
                idx < rows.length - 1
                    ? `border-bottom:1px dashed ${L.rowDash}`
                    : ''
            ].filter(Boolean).join(';');

            const label = document.createElement('div');
            label.style.cssText = [
                `flex:0 0 ${labelW}px`, 'font-weight:800', `font-size:${minutePx}px`,
                `color:${ring.color}`, 'text-align:right', 'padding-right:10px',
                'font-variant-numeric:tabular-nums', 'line-height:1.35',
                'box-sizing:border-box', 'padding-top:2px'
            ].join(';');
            label.textContent = `${ring.minutes}分鐘`;
            row.appendChild(label);

            const divider = document.createElement('div');
            divider.style.cssText = [
                'flex:0 0 1px', 'align-self:stretch', 'width:1px',
                'background:#94a3b8', 'margin:2px 10px 2px 0'
            ].join(';');
            row.appendChild(divider);

            const pills = document.createElement('div');
            pills.style.cssText = [
                'flex:1', 'display:flex', 'flex-wrap:wrap', 'gap:6px',
                'align-items:center', 'align-content:flex-start',
                'min-width:0', 'overflow:visible'
            ].join(';');
            const seenLab = new Set();
            for (const route of ring.routes) {
                const lab = String(route.label || '').trim();
                const labKey = lab.toLowerCase().replace(/\s+/g, '');
                if (!lab || seenLab.has(labKey)) continue;
                seenLab.add(labKey);
                const pill = document.createElement('span');
                const fg = pillTextColor(ring.color);
                pill.style.cssText = [
                    `background:${ring.color}`, `color:${fg}`, `font-size:${pillPx}px`, 'font-weight:700',
                    'padding:4px 10px', 'border-radius:999px', 'line-height:1.3',
                    'white-space:nowrap',
                    fg === '#1a1a1a' ? 'box-shadow:inset 0 0 0 1px rgba(0,0,0,0.12)' : ''
                ].filter(Boolean).join(';');
                pill.textContent = lab;
                pills.appendChild(pill);
            }
            if (!seenLab.size) {
                const empty = document.createElement('span');
                empty.style.cssText = `font-size:${pillPx}px;color:#94a3b8;`;
                empty.textContent = '—';
                pills.appendChild(empty);
            }
            row.appendChild(pills);
            host.appendChild(row);
        });

        document.body.appendChild(host);
        try {
            if (!window.html2canvas) throw new Error('html2canvas 未載入');
            // 先讓瀏覽器算出自然高度，再夾 min／max
            void host.offsetHeight;
            const height = clampBusRouteListHeightPx(host.scrollHeight, L, SCREENSHOT_LAYOUT_DPI);
            host.style.height = `${height}px`;
            const canvas = await html2canvas(host, {
                width,
                height,
                scale: SCREENSHOT_PRINT_SCALE,
                backgroundColor: '#ffffff',
                useCORS: false,
                allowTaint: false
            });
            downloadCanvasPng(canvas, `分時圈_公車報表_${dateStr}.png`);
        } finally {
            host.remove();
        }
    }

    escapeListCell(text) {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /**
     * 實作分時圈分享與 QR Code 生成
     */
    /** 從目前模式 geojson metadata 組 create-share 後備參數 */
    buildShareLookupFromGeo(modeId) {
        const geo = this.omniResults?.[modeId] || (modeId === this.activeTravelMode ? this.currentData : null);
        const sov = geo?.metadata?.sovereign || {};
        const stem = String(sov.stem || '').trim();
        const city = String(sov.city || window.appState?.currentCity || '').trim();
        const district = String(sov.district || window.appState?.currentDistrict || '').trim();
        if (!stem || !city || !district || city === 'Unknown' || district === 'Unknown') return null;
        return { modeId, city, district, stem };
    }

    async shareIsochrone() {
        const modeId = this.activeTravelMode;
        const modeLabel = this.TRAVEL_MODES[modeId]?.label || modeId;
        let relPath = this.modeRelPaths?.[modeId] || null;
        // 舊後端只回 savedFoot：步行失敗時單車有圈卻無 path → 用 metadata 反查
        if (!relPath && modeId === 'foot-walking' && this.currentRelPath) {
            relPath = this.currentRelPath;
        }
        let hash = this.shareHashByMode?.[modeId] || null;
        const lookup = !relPath && !hash ? this.buildShareLookupFromGeo(modeId) : null;

        if (!relPath && !hash && !lookup) {
            this.setStatus(`⚠️ 「${modeLabel}」尚無可分享檔（請先成功繪製該模式）`);
            return;
        }

        this.setStatus(`🔗 正在生成「${modeLabel}」分享連結…`);
        try {
            if (!hash) {
                const body = relPath
                    ? { path: relPath }
                    : lookup;
                const data = await this.fetchIsoJson('/api/isochrones/create-share', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                });
                if (!data.success) throw new Error(data.error || '建立分享失敗');
                hash = data.hash;
                if (data.path) {
                    this.modeRelPaths = { ...(this.modeRelPaths || {}), [modeId]: data.path };
                    this.currentRelPath = data.path;
                } else if (relPath) {
                    this.currentRelPath = relPath;
                }
                this.shareHashByMode = { ...(this.shareHashByMode || {}), [modeId]: hash };
                this.shareHash = hash;
            }
            const fullUrl = `${window.location.origin}/share?h=${hash}`;
            
            const shadow = this.shadowRoot;
            shadow.getElementById('shareUrl').value = fullUrl;
            shadow.getElementById('shareOverlay').style.display = 'flex';

            const qrContainer = shadow.getElementById('qrContainer');
            try {
                await renderBrandedQr(fullUrl, qrContainer, { logoMode: DEFAULT_QR_LOGO_MODE });
            } catch (qrErr) {
                qrContainer.textContent = 'QR 圖產生失敗，請複製上方連結';
                console.warn('[IsoShare] QR', qrErr);
            }
            this.setStatus(`✅ 「${modeLabel}」分享連結已就緒`);
        } catch (err) {
            this.setStatus(`❌ 分享失敗: ${err.message}`);
        }
    }

    clearLayers() {
        if (this.isoLayerGroup) this.map.removeLayer(this.isoLayerGroup);
        if (this.routeLayerGroup) this.map.removeLayer(this.routeLayerGroup);
        const box = document.getElementById('isoLegendBox');
        if (box) box.style.display = 'none';
        
        this.isoLayerGroup = null;
        this.routeLayerGroup = null;
        
        // [v17.5.5] 向下兼容：同步清除全域指標
        window.isochroneLayerGroup = null;
        window.isochroneRouteLayers = [];
    }

    clearAll() {
        this.busOverlayOn = false;
        this._busOverlayToken += 1;
        this.hideBusListPanel();
        this.syncBusButton();
        this.clearLayers();
        this.currentData = null;
        this.currentRelPath = null;
        this.modeRelPaths = {};
        this.shareHashByMode = {};
        this.shareHash = null;
        this.omniResults = null;
        this.activeTravelMode = 'foot-walking';
        this.fillTimeRangeOptions('foot-walking');
        this.shadowRoot?.querySelectorAll('[data-travel-mode]').forEach((btn) => {
            const id = btn.getAttribute('data-travel-mode');
            btn.disabled = id === 'foot-distance-2km';
            btn.classList.toggle('is-active', id === 'foot-walking');
        });
        const input = this.shadowRoot.getElementById('isochroneSearch');
        if (input) input.value = '';
        this.bus.emit(SovereignEvents.ISOC_CLEARED, {});
    }
}

customElements.define('isochrone-cabinet', IsochroneCabinet);

/**
 * [v17.5.0] 主權熱插拔註冊機制
 */
function dock() {
    if (window.appCore) {
        const instance = document.createElement('isochrone-cabinet');
        const container = document.getElementById('socket-isochrone');
        if (container) {
            container.innerHTML = ''; // 清除 placeholder
            container.appendChild(instance);
            window.appCore.registerCabinet('isochrone', instance);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
